import type { QueryClient } from "@tanstack/react-query";
import { createStore, useStore, type StoreApi } from "zustand";

/**
 * UI-only coordination for an alert write whose server outcome is unresolved.
 * Alert data remains exclusively in TanStack Query; this store only keeps the
 * interaction lock and shared Refresh progress needed across page lifecycles.
 */
export type AlertActionVariables =
  | Readonly<{ alertId: string; kind: "acknowledge" }>
  | Readonly<{
      alertId: string;
      assigneeUserId: string;
      dialogToken: number;
      kind: "assign";
    }>
  | Readonly<{
      alertId: string;
      dialogToken: number;
      kind: "mute";
      minutes: number;
    }>
  | Readonly<{ alertId: string; kind: "unmute" }>;

export type ConfirmedMuteEvidence = Readonly<{
  alertId: string;
  mutedUntil: string;
}>;

export type AlertActionRecovery = Readonly<{
  confirmedMute: ConfirmedMuteEvidence | null;
  transport: "confirmed" | "uncertain";
  variables: AlertActionVariables;
}>;

export type AlertActionResolution = Readonly<{
  alertId: string;
  kind: AlertActionVariables["kind"];
  sequence: number;
}>;

interface AlertRecoveryState {
  recovery: AlertActionRecovery | null;
  refreshing: boolean;
  resolution: AlertActionResolution | null;
  clearRecovery: (expected: AlertActionRecovery) => boolean;
  finishRefresh: (expected: AlertActionRecovery) => void;
  recordResolution: (variables: AlertActionVariables) => void;
  recordRecovery: (recovery: AlertActionRecovery) => void;
  resolveRecovery: (expected: AlertActionRecovery) => boolean;
  tryStartRefresh: (expected: AlertActionRecovery) => boolean;
}

export type AlertRecoveryStore = StoreApi<AlertRecoveryState>;

const storesByQueryClient = new WeakMap<QueryClient, AlertRecoveryStore>();

function createAlertRecoveryStore(): AlertRecoveryStore {
  return createStore<AlertRecoveryState>((set) => ({
    recovery: null,
    refreshing: false,
    resolution: null,
    clearRecovery: (expected) => {
      let cleared = false;
      set((state) => {
        if (state.recovery !== expected) return state;
        cleared = true;
        return { recovery: null, refreshing: false };
      });
      return cleared;
    },
    finishRefresh: (expected) =>
      set((state) =>
        state.recovery === expected && state.refreshing
          ? { refreshing: false }
          : state,
      ),
    recordResolution: (variables) =>
      set((state) => ({
        resolution: Object.freeze({
          alertId: variables.alertId,
          kind: variables.kind,
          sequence: (state.resolution?.sequence ?? 0) + 1,
        }),
      })),
    recordRecovery: (recovery) =>
      set((state) =>
        state.recovery === null ? { recovery, refreshing: false } : state,
      ),
    resolveRecovery: (expected) => {
      let resolved = false;
      set((state) => {
        if (state.recovery !== expected) return state;
        resolved = true;
        return {
          recovery: null,
          refreshing: false,
          resolution: Object.freeze({
            alertId: expected.variables.alertId,
            kind: expected.variables.kind,
            sequence: (state.resolution?.sequence ?? 0) + 1,
          }),
        };
      });
      return resolved;
    },
    tryStartRefresh: (expected) => {
      let started = false;
      set((state) => {
        if (state.recovery !== expected || state.refreshing) return state;
        started = true;
        return { refreshing: true };
      });
      return started;
    },
  }));
}

export function alertRecoveryStoreFor(
  queryClient: QueryClient,
): AlertRecoveryStore {
  const existing = storesByQueryClient.get(queryClient);
  if (existing) return existing;
  const store = createAlertRecoveryStore();
  storesByQueryClient.set(queryClient, store);
  return store;
}

export function useAlertRecovery(queryClient: QueryClient) {
  return useStore(alertRecoveryStoreFor(queryClient));
}
